import React from 'react';
import { motion } from 'framer-motion';
import { Truck } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const ShippingReturnsPage: React.FC = () => {
  const { t, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const custom = settings?.shippingContent;
  const language = t.nav.home === 'Home' ? 'en' : 'ka';

  const sections = custom?.sections?.length
    ? custom.sections.map((s: { title: { en: string; ka: string }; text: { en: string; ka: string } }) => ({
        title: localize(s.title),
        text: localize(s.text),
      }))
    : [
        { title: language === 'ka' ? 'მიწოდების პირობები' : 'Shipping Policy', text: language === 'ka' ? 'ჩვენ ვახორციელებთ მიწოდებას საქართველოს მასშტაბით და საერთაშორისო დონეზე. მიწოდების ვადები დამოკიდებულია თქვენს ადგილმდებარეობაზე და არჩეულ მიწოდების მეთოდზე.' : 'We offer shipping throughout Georgia and internationally. Delivery times depend on your location and selected shipping method. All artworks are carefully packaged to ensure safe delivery.' },
        { title: language === 'ka' ? 'მიწოდების ვადები' : 'Delivery Times', text: language === 'ka' ? 'საქართველოში მიწოდება: 2-5 სამუშაო დღე. საერთაშორისო მიწოდება: 7-14 სამუშაო დღე. ექსპრეს მიწოდება ხელმისაწვდომია დამატებითი საფასურით.' : 'Domestic (Georgia): 2-5 business days. International: 7-14 business days. Express shipping is available at an additional cost. You will receive a tracking number once your order has been shipped.' },
        { title: language === 'ka' ? 'შეფუთვა' : 'Packaging', text: language === 'ka' ? 'ყველა ნამუშევარი შეფუთულია პროფესიონალურად, არქივის ხარისხის მასალებით. ჩარჩოიანი ნამუშევრები იგზავნება სპეციალურ დამცავ ყუთებში.' : 'All artworks are professionally packaged using archival-quality materials. Framed pieces are shipped in custom protective boxes. Unframed works are sent in rigid mailers with acid-free tissue paper.' },
        { title: language === 'ka' ? 'დაბრუნების პოლიტიკა' : 'Return Policy', text: language === 'ka' ? 'თუ არ ხართ კმაყოფილი შენაძენით, შეგიძლიათ დაგვიბრუნოთ ნამუშევარი მიღებიდან 14 დღის განმავლობაში. ნამუშევარი უნდა იყოს ორიგინალურ მდგომარეობაში და შეფუთვაში.' : 'If you are not satisfied with your purchase, you may return the artwork within 14 days of receipt. The artwork must be in its original condition and packaging. Please contact us before initiating a return.' },
        { title: language === 'ka' ? 'თანხის დაბრუნება' : 'Refunds', text: language === 'ka' ? 'დაბრუნების მიღების და შემოწმების შემდეგ, თანხა დაგიბრუნდებათ ორიგინალურ გადახდის მეთოდზე 5-10 სამუშაო დღის განმავლობაში. მიწოდების ხარჯები არ ბრუნდება.' : 'Once we receive and inspect the returned artwork, a refund will be issued to your original payment method within 5-10 business days. Shipping costs are non-refundable unless the return is due to our error.' },
        { title: language === 'ka' ? 'დაგვიკავშირდით' : 'Contact Us', text: language === 'ka' ? 'მიწოდებასთან ან დაბრუნებასთან დაკავშირებული კითხვებისთვის მოგვწერეთ: info@fotoatelier.ge ან ეწვიეთ გალერეა ფოტოატელიეს.' : 'For any questions about shipping or returns, please contact us at info@fotoatelier.ge or visit Gallery Fotoatelier.' },
      ];

  const intro = custom?.intro
    ? localize(custom.intro)
    : (language === 'ka'
      ? 'გალერეა ფოტოატელიე უზრუნველყოფს თქვენი ნამუშევრების უსაფრთხო მიწოდებას და მარტივ დაბრუნების პროცესს.'
      : 'Gallery Fotoatelier ensures safe delivery of your artworks and a hassle-free return process.');

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-20 px-4 sm:px-6 md:px-24">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-10 sm:mb-16"
        >
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
            {language === 'ka' ? 'მიწოდება და დაბრუნება' : 'Shipping & Returns'}
          </h1>
          <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.2em] text-black/25 dark:text-white/25">
            {t.legal.lastUpdated}
          </p>
        </motion.div>

        {intro && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-10 p-6 sm:p-8 rounded-2xl border border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#0a0a0a]"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center shrink-0">
                <Truck size={18} className="text-black/40 dark:text-white/40" />
              </div>
              <p className="text-sm sm:text-base text-black/60 dark:text-white/60 font-light leading-relaxed">
                {intro}
              </p>
            </div>
          </motion.div>
        )}

        <div className="space-y-8">
          {sections.map((section: { title: string; text: string }, i: number) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.6 }}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-6 h-px bg-black/10 dark:bg-white/10" />
                <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">
                  {section.title}
                </h2>
              </div>
              <p className="text-sm sm:text-[15px] text-black/55 dark:text-white/55 font-light leading-[1.8] pl-9">
                {section.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ShippingReturnsPage;
